/**
 * Saved campaign content projection for PRD004.6.
 *
 * The builder stores a versioned content snapshot inside the existing campaign_settings JSON;
 * CampaignPage renders only that server-returned snapshot so navigation/reload reflects persisted
 * data. Campaign authorization belongs to the CampaignService/provider request; this view only
 * receives the already-authorized response. It does not fetch, mutate, or infer a draft and safely
 * omits legacy or unsupported schema versions rather than presenting guessed content.
 */
import React from 'react';
import { Box, Heading, Text, VStack } from '@chakra-ui/react';

export interface CampaignContentSettingSnapshot {
  id: string;
  version: number;
  provenance: 'mythbound-original' | 'user-authored';
  title: string;
  description: string;
}

export interface CampaignContentAdventureSnapshot {
  id: string;
  version: number;
  provenance: 'mythbound-original' | 'user-authored';
  title: string;
  hook: string;
  antagonist?: string;
}

export interface CampaignContentSnapshot {
  schema_version: 1;
  setting: CampaignContentSettingSnapshot;
  adventure: CampaignContentAdventureSnapshot;
}

export function readCampaignContent(settings: unknown): CampaignContentSnapshot | null {
  if (!settings || typeof settings !== 'object') return null;
  const content = (settings as Record<string, unknown>).content;
  if (!content || typeof content !== 'object') return null;
  const candidate = content as Record<string, unknown>;
  const setting = candidate.setting as Record<string, unknown> | undefined;
  const adventure = candidate.adventure as Record<string, unknown> | undefined;
  if (
    candidate.schema_version !== 1 || !setting || !adventure ||
    typeof setting.id !== 'string' || typeof setting.title !== 'string' || typeof setting.description !== 'string' ||
    typeof setting.version !== 'number' || !Number.isSafeInteger(setting.version) || setting.version < 1 ||
    (setting.provenance !== 'mythbound-original' && setting.provenance !== 'user-authored') ||
    typeof adventure.id !== 'string' || typeof adventure.title !== 'string' || typeof adventure.hook !== 'string' ||
    typeof adventure.version !== 'number' || !Number.isSafeInteger(adventure.version) || adventure.version < 1 ||
    (adventure.provenance !== 'mythbound-original' && adventure.provenance !== 'user-authored') ||
    (adventure.antagonist !== undefined && typeof adventure.antagonist !== 'string')
  ) return null;
  // Reconstruct the validated display fields; arbitrary settings keys never become UI props.
  return {
    schema_version: 1,
    setting: { id: setting.id, version: setting.version, provenance: setting.provenance,
      title: setting.title, description: setting.description },
    adventure: { id: adventure.id, version: adventure.version, provenance: adventure.provenance,
      title: adventure.title, hook: adventure.hook,
      ...(typeof adventure.antagonist === 'string' ? { antagonist: adventure.antagonist } : {}) },
  };
}

export const CampaignContentSummary: React.FC<{ campaignSettings: unknown }> = ({ campaignSettings }) => {
  const content = readCampaignContent(campaignSettings);
  if (!content) return null;
  return (
    <Box role="region" aria-label="Saved campaign content" bg="#1a1a20" borderRadius="md" p={5} mb={8}>
      <Heading size="md" color="#D9B555" mb={4}>Campaign Content</Heading>
      <VStack align="stretch" spacing={4}>
        <Box>
          <Text color="#C5C5C7" fontSize="sm">Setting · {content.setting.id} v{content.setting.version} · {content.setting.provenance}</Text>
          <Heading size="sm" color="#fff" mt={1}>{content.setting.title}</Heading>
          <Text color="#C5C5C7" mt={1} whiteSpace="pre-wrap">{content.setting.description}</Text>
        </Box>
        <Box>
          <Text color="#C5C5C7" fontSize="sm">Adventure · {content.adventure.id} v{content.adventure.version} · {content.adventure.provenance}</Text>
          <Heading size="sm" color="#fff" mt={1}>{content.adventure.title}</Heading>
          <Text color="#C5C5C7" mt={1} whiteSpace="pre-wrap">{content.adventure.hook}</Text>
          {content.adventure.antagonist && (
            <Text color="#C5C5C7" mt={1}>Opposition: {content.adventure.antagonist}</Text>
          )}
        </Box>
      </VStack>
    </Box>
  );
};

export default CampaignContentSummary;
